import React from 'react'
import { useTranslation } from 'react-i18next'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { AppForm, FormTextField } from 'src/components/FormCore'
import { ShowDialogProps } from 'src/show-dialog'
import { useZodForm } from 'src/hooks/zod-form'
import { z } from 'zod'
import { Box } from '@mui/material'
import { CancelButton } from 'src/components/CancelButton'
import { MainButton } from '@lovoldsystem/ui'
import { IUser } from 'src/generated-types'
import { useAddProjectUser } from 'src/api/user'
import { toast } from 'react-toastify'
import { ALREADY_EXISTS } from 'src/constant'

interface CreateContactDialogProps extends ShowDialogProps {
  initialName?: string
  onCreateUser: (user: IUser) => void
}

export const CreateContactDialog: React.FC<CreateContactDialogProps> = ({ pop, initialName, onCreateUser }) => {
  const { t: translate } = useTranslation(['projects', 'common', 'users'])
  const { mutateAsync } = useAddProjectUser()

  const contactSchema = z.object({
    firstName: z.string().min(1, translate('required', { ns: 'common' })),
    lastName: z.string().optional(),
    email: z
      .string()
      .min(1, translate('required', { ns: 'common' }))
      .email(),
    phoneNumber: z.string().optional(),
  })

  const parseInitialName = (name?: string) => {
    if (!name) return { firstName: '', lastName: '', email: '' }
    if (name.includes('@')) return { firstName: '', lastName: '', email: name }
    const parts = name.trim().split(' ')
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: '', email: '' }
    }
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(' '),
      email: '',
    }
  }

  const { firstName, lastName, email } = parseInitialName(initialName)

  const form = useZodForm(contactSchema, {
    defaultValues: {
      firstName,
      lastName,
      email,
      phoneNumber: '',
    },
  })

  const handleSubmit = (form: any) => {
    mutateAsync(form, {
      onSuccess: data => {
        onCreateUser(data.data)
        toast.success(translate('contact-created', { ns: 'projects' }))
        pop(data)
      },
      onError: err => {
        if (err['data'].message === ALREADY_EXISTS) {
          toast.error(translate('contact-already-exists', { ns: 'projects' }))
          return
        }
        toast.error(translate('contact-creation-failed', { ns: 'projects' }))
      },
    })
  }

  return (
    <GenericDialogLayout title={translate('create-contact', { ns: 'projects' })} pop={pop}>
      <AppForm form={form}>
        <FormTextField name="firstName" label={translate('firstName', { ns: 'users' })} autoFocus />
        <FormTextField name="lastName" label={translate('lastName', { ns: 'users' })} />
        <FormTextField name="email" label={translate('email', { ns: 'users' })} type="email" />
        <FormTextField name="phoneNumber" label={translate('phoneNumber', { ns: 'users' })} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
          <CancelButton onClick={() => pop()} />
          <MainButton onClick={() => form.handleSubmit(handleSubmit)()}>
            {translate('create', { ns: 'common' })}
          </MainButton>
        </Box>
      </AppForm>
    </GenericDialogLayout>
  )
}
